import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { PlusComponent } from 'src/app/components/icons/plus';
import { TrashComponent } from 'src/app/components/icons/trash';
import { LocaleService } from 'src/app/services/locale';
import { CategoryService } from 'src/app/services/category';
import { CountryPickerComponent } from 'src/app/components/shared/country-picker';
import { YearPickerComponent } from 'src/app/components/shared/year-picker';

@Component({
  selector: 'app-category5-question1',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    PlusComponent,
    TrashComponent,
    CountryPickerComponent,
    YearPickerComponent,
  ],
  template: `
    <div class="flex flex-wrap gap-4 sm:gap-6 mt-3">
      <div
        (click)="setUsage(true)"
        class="flex items-center cursor-pointer gap-2"
      >
        <div
          [ngClass]="{ 'bg-[#183A25]': usage }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].yes }}</div>
      </div>
      <div
        (click)="setUsage(false)"
        class="flex items-center cursor-pointer gap-2"
      >
        <div
          [ngClass]="{ 'bg-[#183A25]': !usage }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].no }}</div>
      </div>
    </div>

    <form [formGroup]="form" *ngIf="usage" class="mt-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">
          {{ strings[language].productGroups }}
        </h3>
        <div
          (click)="addProductGroup()"
          class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
        >
          <app-plus class="w-4 h-4"></app-plus>
        </div>
      </div>

      <div>
        <div
          *ngFor="let group of productGroups.controls; let i = index"
          [formGroup]="getFormGroup(group)"
          class="border rounded-lg p-4 bg-white shadow-sm mb-4"
        >
          <div class="flex justify-between items-center mb-4">
            <h3 class="font-semibold">
              {{ strings[language].productGroup }} {{ i + 1 }}
            </h3>
            <button
              type="button"
              (click)="removeProductGroup(i)"
              class="text-red-500 hover:text-red-700"
            >
              <app-trash></app-trash>
            </button>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <app-input
              [label]="strings[language].productLabel"
              [control]="getFormControl(group, 'product_label')"
              [placeholder]="strings[language].productPlaceholder"
            ></app-input>
            <app-input
              (change)="formatAmount(i)"
              [label]="strings[language].unitsSold"
              type="text"
              [control]="getFormControl(group, 'N_sold')"
              [placeholder]="strings[language].unitsPlaceholder"
              [required]="true"
            ></app-input>
          </div>

          <div class="gap-4 mb-4 flex">
            <div class="flex flex-col gap-2 relative w-1/2">
              <label class="text-[#616161]">{{
                strings[language].unitType
              }}</label>
              <span
                [style.pointer-events]="
                  !getFormControl(group, 'N_sold').value ? 'none' : 'auto'
                "
                [style.opacity]="
                  !getFormControl(group, 'N_sold').value ? '0.5' : '1'
                "
              >
                <app-dropdown
                  [options]="unitTypes"
                  [control]="getFormControl(group, 'units_sold_unit')"
                  [placeholder]="strings[language].selectUnit"
                  [required]="true"
                ></app-dropdown>
              </span>
            </div>
            <div class="w-1/2">
              <div class="flex flex-col gap-2 relative">
                <label class="text-[#616161]">{{
                  strings[language].selectRegion
                }}</label>
                <app-country-picker
                  [control]="getFormControl(group, 'region')"
                ></app-country-picker>
              </div>
            </div>
          </div>
          <div class="gap-4 mb-4 flex">
            <div class="flex flex-col gap-2 relative w-1/2">
              <label class="text-[#616161]">{{
                strings[language].reportingYear
              }}</label>
              <app-year-picker
                [control]="getFormControl(group, 'reportingYear')"
              ></app-year-picker>
            </div>
            <div class="w-1/2">
              <div class="flex flex-col gap-2 relative">
                <label class="text-[#616161]">
                  {{ strings[language].notes }}
                </label>
                <textarea
                  class="border border-gray-300 rounded p-2 text-sm w-full"
                  [formControl]="getFormControl(group, 'note')"
                  rows="3"
                  [placeholder]="strings[language].notesPlaceholder"
                  maxlength="500"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <div class="flex flex-col gap-4 mt-3">
              <!-- Direct methods -->
              <label
                class="block text-sm font-medium text-gray-700 mb-1 font-semibold"
              >
                {{ strings[language].directMethods }}
              </label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <!-- M1 -->
                <div
                  class="flex items-center gap-2  cursor-pointer"
                  (click)="toggleCheckbox(group, 'use_m1')"
                >
                  <div
                    class="w-5 h-5 checkbox border border-gray-500 rounded-sm flex items-center justify-center"
                    [ngClass]="{ 'bg-[#183A25]': group.get('use_m1')?.value }"
                  >
                    <svg
                      *ngIf="group.get('use_m1')?.value"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3   text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="flex items-center">
                    <h4 class="label-text">
                      M1 - {{ strings[language].m1Label }}
                    </h4>
                    <div class="relative group ml-1 mt-1">
                      <button
                        type="button"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none"
                        (click)="$event.stopPropagation()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div
                        class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                      >
                        <p class="mb-2">
                          <b>{{ strings[language].method_label1 }}:</b>
                          {{ strings[language].m1_label1 }}
                        </p>
                        <p class="mb-2">
                          <b>{{ strings[language].method_label2 }}:</b>
                          {{ strings[language].m1_label2 }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- M2 -->
                <div
                  class="flex items-center gap-2 cursor-pointer"
                  (click)="toggleCheckbox(group, 'use_m2')"
                >
                  <div
                    class="w-5 h-5 chk border border-gray-500 rounded-sm flex items-center justify-center"
                    [ngClass]="{ 'bg-[#183A25]': group.get('use_m2')?.value }"
                  >
                    <svg
                      *ngIf="group.get('use_m2')?.value"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3  text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="flex items-center">
                    <h4 class="label-text">
                      M2 - {{ strings[language].m2Label }}
                    </h4>
                    <div class="relative group ml-1 mt-1">
                      <button
                        type="button"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none"
                        (click)="$event.stopPropagation()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5 "
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div
                        class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                      >
                        <p class="mb-2">
                          <b>{{ strings[language].method_label1 }}:</b>
                          {{ strings[language].m2_label1 }}
                        </p>
                        <p class="mb-2">
                          <b>{{ strings[language].method_label2 }}:</b>
                          {{ strings[language].m2_label2 }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- M3 -->
                <div
                  class="flex items-center gap-2 cursor-pointer"
                  (click)="toggleCheckbox(group, 'use_m3')"
                >
                  <div
                    class="w-5 h-5   border border-gray-500 rounded-sm flex items-center justify-center"
                    [ngClass]="{ 'bg-[#183A25]': group.get('use_m3')?.value }"
                  >
                    <svg
                      *ngIf="group.get('use_m3')?.value"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3  text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="flex items-center">
                    <h4 class="label-text">
                      M3 - {{ strings[language].m3Label }}
                    </h4>
                    <div class="relative group ml-1 mt-1">
                      <button
                        type="button"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none"
                        (click)="$event.stopPropagation()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div
                        class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                      >
                        <p class="mb-2">
                          <b>{{ strings[language].method_label1 }}:</b>
                          {{ strings[language].m3_label1 }}
                        </p>
                        <p class="mb-2">
                          <b>{{ strings[language].method_label2 }}:</b>
                          {{ strings[language].m3_label2 }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Indirect methods -->
              <label
                class="block text-sm font-medium text-gray-700 mb-1 font-semibold"
              >
                {{ strings[language].indirectMethods }}
              </label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <!-- M4 -->
                <div
                  class="flex items-center gap-2 cursor-pointer"
                  (click)="toggleCheckbox(group, 'use_m4')"
                >
                  <div
                    class="w-5 h-5 checkbox border border-gray-500 rounded-sm flex items-center justify-center"
                    [ngClass]="{ 'bg-[#183A25]': group.get('use_m4')?.value }"
                  >
                    <svg
                      *ngIf="group.get('use_m4')?.value"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3  text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="flex items-center">
                    <h4 class="label-text">
                      M4 - {{ strings[language].m4Label }}
                    </h4>
                    <div class="relative group ml-1 mt-1">
                      <button
                        type="button"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none"
                        (click)="$event.stopPropagation()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div
                        class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                      >
                        <p class="mb-2">
                          <b>{{ strings[language].method_label1 }}:</b>
                          {{ strings[language].m4_label1 }}
                        </p>
                        <p class="mb-2">
                          <b>{{ strings[language].method_label2 }}:</b>
                          {{ strings[language].m4_label2 }}<br /><br />
                          {{ strings[language].m4_label12 }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- M5 -->
                <div
                  class="flex items-center gap-2 cursor-pointer"
                  (click)="toggleCheckbox(group, 'use_m5')"
                >
                  <div
                    class="w-5 h-5  border border-gray-500 rounded-sm flex items-center justify-center"
                    [ngClass]="{ 'bg-[#183A25]': group.get('use_m5')?.value }"
                  >
                    <svg
                      *ngIf="group.get('use_m5')?.value"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3  text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div class="flex items-center">
                    <h4 class="label-text">
                      M5 - {{ strings[language].m5Label }}
                    </h4>
                    <div class="relative group ml-1 mt-1">
                      <button
                        type="button"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none"
                        (click)="$event.stopPropagation()"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div
                        class="absolute z-10 hidden group-hover:block w-[400px] bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                      >
                        <p class="mb-2">
                          <b>{{ strings[language].method_label1 }}:</b>
                          {{ strings[language].m5_label1 }}
                        </p>
                        <p class="mb-2">
                          <b>{{ strings[language].method_label2 }}:</b>
                          {{ strings[language].m5_label2 }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Method 1 Section -->
          <div
            *ngIf="getFormControl(group, 'use_m1').value"
            class="mt-4 p-4 bg-[#043B22]/5 rounded-lg border"
          >
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold">
                M1 – {{ strings[language].m1Label }}
              </h4>
              <button
                type="button"
                class="text-blue-500 text-sm flex items-center gap-1"
                (click)="addMethod(group, 'm1_methods', createM1Method())"
              >
                <div class="px-3 py-2">
                  <app-plus class="w-4 h-4"></app-plus>
                </div>
              </button>
            </div>

            <div formArrayName="m1_methods">
              <div
                *ngFor="
                  let method of getMethodArray(group, 'm1_methods').controls;
                  let j = index
                "
                [formGroup]="getFormGroup(method)"
                class="mb-4 p-4 bg-white rounded border border-gray-200"
              >
                <div class="flex items-center justify-between mb-5">
                  <h4 class="font-semibold"></h4>
                  <button
                    type="button"
                    class="text-red-500 hover:text-red-700"
                    (click)="removeMethod(group, 'm1_methods', j)"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <app-dropdown
                      [label]="strings[language].fuelType"
                      [options]="fuelTypesM1"
                      [control]="getFormControl(method, 'fuel_type')"
                      [placeholder]="strings[language].selectFuelType"
                      (change)="updateFuelUnitsM3(method, 'fuel_type'); save()"
                    ></app-dropdown>
                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelTypeRequired']"
                      class="text-red-500 text-sm -mt-3 mb-2 col-span-2"
                    >
                      {{ strings[language].fuelTypeRequiredError2 }}
                    </div>
                  </div>
                  <app-input
                    [label]="strings[language].lifetimeFuelUse"
                    type="number"
                    [control]="getFormControl(method, 'fuel_lifetime_quantity')"
                    [placeholder]="strings[language].fuelUsePlaceholder"
                    [required]="!!getFormControl(method, 'fuel_type').value"
                  ></app-input>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <app-dropdown
                      [label]="strings[language].fuelUnit"
                      [options]="fuelUnitsM3"
                      [control]="getFormControl(method, 'fuel_lifetime_unit')"
                      [placeholder]="strings[language].selectUnit"
                      [required]="!!getFormControl(method, 'fuel_type').value"
                    >
                    </app-dropdown>
                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelUnitRequired']"
                      class="text-red-500 mt-1 text-sm -mt-3 mb-2 col-span-2"
                    >
                      {{ strings[language].fuelUnitRequiredError2 }}
                    </div>
                  </div>

                  <app-input
                    [label]="strings[language].lifetimeElectricityUse"
                    type="number"
                    [control]="getFormControl(method, 'elec_lifetime_kwh')"
                    [placeholder]="strings[language].electricityUsePlaceholder"
                    [required]="
                      !getFormControl(method, 'fuel_type').value &&
                      !getFormControl(method, 'elec_lifetime_kwh').value
                    "
                  ></app-input>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <app-dropdown
                    [label]="strings[language].refrigerantGasType"
                    [options]="refrigerantTypesM1"
                    [control]="getFormControl(method, 'ref_gas_type')"
                    [placeholder]="strings[language].selectRefrigerant"
                    [required]="!!getFormControl(method, 'ref_charge_kg').value"
                  ></app-dropdown>
                  <app-input
                    [label]="strings[language].refrigerantCharge"
                    type="number"
                    [control]="getFormControl(method, 'ref_charge_kg')"
                    [placeholder]="
                      strings[language].refrigerantChargePlaceholder
                    "
                  ></app-input>
                  <app-input
                    [label]="strings[language].refrigerantLeakPct"
                    type="number"
                    [control]="getFormControl(method, 'ref_leak_pct')"
                    [placeholder]="strings[language].leakPctPlaceholder"
                    [required]="!!getFormControl(method, 'ref_charge_kg').value"
                  ></app-input>
                </div>
              </div>
            </div>
          </div>

          <!-- Method 2 Section -->
          <div
            *ngIf="getFormControl(group, 'use_m2').value"
            class="mt-4 p-4 bg-[#043B22]/5 rounded-lg border"
          >
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold">
                M2 – {{ strings[language].m2Label }}
              </h4>
              <button
                type="button"
                class="text-blue-500 text-sm flex items-center gap-1"
                (click)="addMethod(group, 'm2_methods', createM2Method())"
              >
                <div class="px-3 py-2">
                  <app-plus class="w-4 h-4"></app-plus>
                </div>
              </button>
            </div>

            <div formArrayName="m2_methods">
              <div
                *ngFor="
                  let method of getMethodArray(group, 'm2_methods').controls;
                  let j = index
                "
                [formGroup]="getFormGroup(method)"
                class="mb-4 p-4 bg-white rounded border border-gray-200"
              >
                <div class="flex justify-end">
                  <button
                    type="button"
                    class="text-red-500 hover:text-red-700"
                    (click)="removeMethod(group, 'm2_methods', j)"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <app-dropdown
                    [label]="strings[language].fuelFeedstockType"
                    [options]="fuelTypesM2"
                    [control]="getFormControl(method, 'fuel_type')"
                    [placeholder]="strings[language].selectFuelType"
                    [required]="true"
                    (change)="updateFuelUnitsM2(method, 'fuel_type', j); save()"
                  ></app-dropdown>
                  <div>
                    <app-input
                      [label]="strings[language].quantitySold"
                      type="number"
                      [control]="getFormControl(method, 'fuel_qty')"
                      [placeholder]="strings[language].quantityPlaceholder"
                    ></app-input>
                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelQtyRequired']"
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].quantityRequiredError }}
                    </div>
                  </div>
                  <div>
                    <app-dropdown
                      [label]="strings[language].fuelUnit"
                      [options]="fuelUnitsM2[i] || fuelUnits"
                      [control]="getFormControl(method, 'fuel_unit')"
                      [placeholder]="strings[language].selectUnit"
                      [required]="!!getFormControl(method, 'fuel_type').value"
                    >
                    </app-dropdown>
                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelUnitRequired']"
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].unitRequiredError }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Method 3 Section -->
          <div
            *ngIf="getFormControl(group, 'use_m3').value"
            class="mt-4 p-4 bg-[#043B22]/5 rounded-lg border"
          >
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold">
                M3 – {{ strings[language].m3Label }}
              </h4>
              <button
                type="button"
                class="text-blue-500 text-sm flex items-center gap-1"
                (click)="addMethod(group, 'm3_methods', createM3Method())"
              >
                <div class="px-3 py-2">
                  <app-plus class="w-4 h-4"></app-plus>
                </div>
              </button>
            </div>

            <div formArrayName="m3_methods">
              <div
                *ngFor="
                  let method of getMethodArray(group, 'm3_methods').controls;
                  let j = index
                "
                [formGroup]="getFormGroup(method)"
                class="mb-4 p-4 bg-white rounded border border-gray-200"
              >
                <div class="flex justify-end">
                  <button
                    type="button"
                    class="text-red-500 hover:text-red-700"
                    (click)="removeMethod(group, 'm3_methods', j)"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <app-dropdown
                    [label]="strings[language].gasType"
                    [options]="refrigerantTypes"
                    [control]="getFormControl(method, 'gas_type')"
                    [placeholder]="strings[language].selectGasType"
                    [required]="true"
                  ></app-dropdown>
                  <div>
                    <app-input
                      [label]="strings[language].gasCharge"
                      type="number"
                      [control]="getFormControl(method, 'gas_charge_kg')"
                      [placeholder]="strings[language].gasChargePlaceholder"
                    ></app-input>
                    <div
                      *ngIf="getFormGroup(method).errors?.['gasChargeRequired']"
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].gasChargeRequiredError }}
                    </div>
                  </div>
                  <div>
                    <div class="flex gap-1">
                      <label class="text-[#616161]">{{
                        strings[language].gasLeakPct
                      }}</label>
                      <div class="relative group ml-1 mt-1">
                        <button
                          type="button"
                          class="text-gray-500 hover:text-gray-700 focus:outline-none "
                          (click)="$event.stopPropagation()"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                        <div
                          class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
                        >
                          {{ strings[language].tooltip }}
                        </div>
                      </div>
                    </div>
                    <app-input
                      type="number"
                      [control]="getFormControl(method, 'gas_leak_pct')"
                      [placeholder]="strings[language].leakPctPlaceholder"
                    ></app-input>
                    <div
                      *ngIf="
                        getFormGroup(method).errors?.['gasLeakPctRequired']
                      "
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].gasLeakPctRequiredError }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Method 4 Section -->
          <div
            *ngIf="getFormControl(group, 'use_m4').value"
            class="mt-4 p-4 bg-[#043B22]/5 rounded-lg border"
          >
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold">
                M4 – {{ strings[language].m4Label }}
              </h4>
              <button
                type="button"
                class="text-blue-500 text-sm flex items-center gap-1"
                (click)="addMethod(group, 'm4_methods', createM4Method())"
              >
                <div class="px-3 py-2">
                  <app-plus class="w-4 h-4"></app-plus>
                </div>
              </button>
            </div>

            <div class="grid grid-cols-1 gap-4 mb-4 bg">
              <label class="text-[#616161]">{{
                strings[language].lifetimeFunctionalUses
              }}</label>
              <span class="bg-white">
                <app-input
                  type="number"
                  [control]="getFormControl(group, 'U_life')"
                  [placeholder]="strings[language].functionalUsesPlaceholder"
                  [required]="true"
                ></app-input>
              </span>
            </div>

            <div formArrayName="m4_methods">
              <div
                *ngFor="
                  let method of getMethodArray(group, 'm4_methods').controls;
                  let j = index
                "
                [formGroup]="getFormGroup(method)"
                class="mb-4 p-4 bg-white rounded border border-gray-200"
              >
                <div class="flex justify-end">
                  <button
                    type="button"
                    class="text-red-500 hover:text-red-700"
                    (click)="removeMethod(group, 'm4_methods', j)"
                  >
                    <app-trash></app-trash>
                  </button>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <app-input
                    [label]="strings[language].scenarioLabel"
                    [control]="getFormControl(method, 'scn_label')"
                    [placeholder]="strings[language].scenarioLabelPlaceholder"
                    maxlength="50"
                    [required]="true"
                  ></app-input>
                  <app-input
                    [label]="strings[language].scenarioSharePct"
                    type="number"
                    [control]="getFormControl(method, 'scn_share_pct')"
                    [placeholder]="strings[language].pctPlaceholder"
                    [required]="true"
                  ></app-input>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <app-dropdown
                      [label]="strings[language].fuelTypeIfAny"
                      [options]="fuelTypesM2"
                      [control]="getFormControl(method, 'scn_fuel_type')"
                      [placeholder]="strings[language].selectFuelType"
                      (change)="
                        updateFuelUnitsM2(method, 'scn_fuel_type', j); save()
                      "
                    ></app-dropdown>
                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelTypeRequired']"
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].fuelTypeRequiredError }}
                    </div>
                  </div>
                  <app-input
                    [label]="strings[language].fuelPerUse"
                    type="number"
                    [control]="getFormControl(method, 'scn_fuel_qty')"
                    [placeholder]="strings[language].fuelPerUsePlaceholder"
                  ></app-input>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <app-dropdown
                      [label]="strings[language].fuelUnit"
                      [options]="fuelUnitsM2[j] || fuelUnits"
                      [control]="getFormControl(method, 'scn_fuel_unit')"
                      [placeholder]="strings[language].selectUnit"
                    ></app-dropdown>

                    <div
                      *ngIf="getFormGroup(method).errors?.['fuelUnitRequired']"
                      class="text-red-500 text-sm mt-1"
                    >
                      {{ strings[language].fuelUnitRequiredError }}
                    </div>
                  </div>
                  <app-input
                    [label]="strings[language].electricityPerUse"
                    type="number"
                    [control]="getFormControl(method, 'scn_elec_kwh')"
                    [placeholder]="
                      strings[language].electricityPerUsePlaceholder
                    "
                  ></app-input>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <app-input
                    [label]="strings[language].refrigerantLeakedPerUse"
                    type="number"
                    [control]="getFormControl(method, 'scn_ref_leak_kg')"
                    [placeholder]="strings[language].refrigerantLeakPlaceholder"
                  ></app-input>
                  <app-dropdown
                    [label]="strings[language].refrigerantGasType"
                    [options]="refrigerantTypesM3"
                    [control]="getFormControl(method, 'scn_ref_gas_type')"
                    [placeholder]="strings[language].selectRefrigerant"
                    [required]="
                      !!getFormControl(method, 'scn_ref_leak_kg').value
                    "
                  ></app-dropdown>
                  <app-input
                    [label]="strings[language].extraGHGPerUse"
                    type="number"
                    [control]="getFormControl(method, 'scn_ghg_kg')"
                    [placeholder]="strings[language].ghgPerUsePlaceholder"
                  ></app-input>
                  <app-dropdown
                    [label]="strings[language].ghgType"
                    [options]="refrigerantTypesM3"
                    [control]="getFormControl(method, 'scn_ghg_type')"
                    [placeholder]="strings[language].selectGHGType"
                    [required]="!!getFormControl(method, 'scn_ghg_kg').value"
                  ></app-dropdown>
                </div>
              </div>
              <div
                *ngIf="
                  getMethodArray(group, 'm4_methods').errors?.[
                    'scenarioSumInvalid'
                  ]
                "
                class="text-red-500 text-sm"
              >
                <!-- {{ strings[language].scenarioSumError }} -->
              </div>
            </div>
          </div>

          <!-- Method 5 Section -->
          <div
            *ngIf="getFormControl(group, 'use_m5').value"
            class="mt-4 p-4 bg-[#043B22]/5 rounded-lg border"
          >
            <h4 class="font-semibold mb-3">
              M5 – {{ strings[language].m5Label }}
            </h4>
            <div class="mb-4 p-4 bg-white rounded border border-gray-200">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <app-input
                  [label]="strings[language].intermediateProductLabel"
                  [control]="getFormControl(group, 'intermed_label')"
                  [placeholder]="
                    strings[language].intermediateProductPlaceholder
                  "
                  maxlength="250"
                  [required]="true"
                ></app-input>
                <div class="flex flex-col gap-2 relative">
                  <label class="text-[#616161]" style="font-size: 15px;">
                    {{ strings[language].unitsIntermediateSold }}
                  </label>
                  <app-input
                    type="number"
                    [control]="getFormControl(group, 'intermed_sold')"
                    [placeholder]="strings[language].unitsSoldPlaceholder"
                    [required]="true"
                  ></app-input>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <app-dropdown
                  [label]="strings[language].unitType"
                  [options]="unitTypes"
                  [control]="getFormControl(group, 'intermed_unit')"
                  [placeholder]="strings[language].selectUnit"
                  [required]="true"
                ></app-dropdown>
                <app-input
                  [label]="strings[language].lifetimeUsesFinalProduct"
                  type="number"
                  [control]="getFormControl(group, 'U_life_final')"
                  [placeholder]="strings[language].lifetimeUsesPlaceholder"
                  [required]="true"
                ></app-input>
                <app-input
                  [label]="strings[language].emissionFactorPerUse"
                  type="number"
                  [control]="getFormControl(group, 'EF_per_use_intermed')"
                  [placeholder]="strings[language].emissionFactorPlaceholder"
                  [required]="true"
                ></app-input>
              </div>
            </div>
            <!-- <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ strings[language].notes }}
              </label>
              <textarea
                class="border border-gray-300 rounded p-2 text-sm w-full"
                [formControl]="getFormControl(group, 'note')"
                rows="3"
                [placeholder]="strings[language].notesPlaceholder"
                maxlength="500"
              ></textarea>
            </div> -->
          </div>
        </div>
      </div>
    </form>
  `,
})
export class Category5Question1Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  fb = inject(FormBuilder);

  usage = false;
  cat5_1_direct_total = 0;

  form = this.fb.group({
    productGroups: this.fb.array([]),
  });

  get productGroups(): FormArray {
    return this.form.get('productGroups') as FormArray;
  }

  // Dropdown options
  unitTypes = [
    { value: 'l', label: 'L' },
    { value: 'm3', label: 'm³' },
    { value: 'kg', label: 'kg' },
    { value: 't', label: 't' },
    { value: 'kWh', label: 'kWh' },
    { value: 'MJ', label: 'MJ' },
    { value: 'unit', label: 'Unit' },
  ];

  fuelTypesM1 = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'petrol', label: 'Petrol' },
    { value: 'natural_gas', label: 'Natural Gas' },
    { value: 'coal', label: 'Coal' },
    { value: 'LPG', label: 'LPG' },
    { value: 'wood_pellets_bio_100', label: 'Wood pellets (bio 100%)' },
    { value: 'hydrogen', label: 'Hydrogen' },
  ];

  fuelTypesM2 = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'petrol', label: 'Petrol' },
    { value: 'natural_gas', label: 'Natural Gas' },
    { value: 'LPG', label: 'LPG' },
    { value: 'coal', label: 'Coal' },
    { value: 'kerosene', label: 'Kerosene' },
    { value: 'heavy_fuel_oil', label: 'Heavy Fuel Oil' },
    { value: 'wood_pellets_bio_100', label: 'Wood Pellets (100% bio)' },
    { value: 'hydrogen', label: 'Hydrogen' },
  ];

  fuelUnits = [
    { value: 'l', label: 'L' },
    { value: 'm3', label: 'm³' },
    { value: 'kg', label: 'kg' },
    { value: 't', label: 't' },
    { value: 'kWh', label: 'kWh' },
    { value: 'MJ', label: 'MJ' },
  ];

  fuelTypeCategoryMap: {
    [key: string]: 'liquid' | 'gas' | 'solid' | 'energy';
  } = {
    diesel: 'liquid',
    petrol: 'liquid',
    kerosene: 'liquid',
    heavy_fuel_oil: 'liquid',
    LPG: 'gas',
    natural_gas: 'gas',
    hydrogen: 'energy',
    coal: 'solid',
    wood_pellets_bio_100: 'solid',
  };
  unitCategoryMap: {
    [key in 'liquid' | 'gas' | 'solid' | 'energy']: {
      value: string;
      label: string;
    }[];
  } = {
    liquid: [{ value: 'l', label: 'L' }],
    gas: [
      { value: 'm3', label: 'm³' },
      { value: 'MJ', label: 'MJ' },
      { value: 'kWh', label: 'kWh' },
    ],
    solid: [
      { value: 'kg', label: 'kg' },
      { value: 'unit', label: 'Unit' },
      { value: 't', label: 't' },
    ],
    energy: [
      { value: 'MJ', label: 'MJ' },
      { value: 'kWh', label: 'kWh' },
    ],
  };

  fuelMeasurementBasisMap: Record<any, 'liquid' | 'gaseous' | 'solid'> = {
    diesel: 'liquid',
    petrol: 'liquid',
    LPG: 'liquid',
    natural_gas: 'gaseous',
    biomass: 'solid', // If wood pellets are considered biomass
    hydrogen: 'solid', // Or 'gaseous' if applicable
    coal: 'solid', // Explicitly set coal as solid
  };

  fuelUnitsM1 = [
    { value: 'l', label: 'L' },
    { value: 'm3', label: 'm³' },
    { value: 'kg', label: 'kg' },
    { value: 't', label: 't' },
    { value: 'MJ', label: 'MJ' },
  ];

  refrigerantTypes = [
    { value: 'HFC-134a', label: 'HFC-134a' },
    { value: 'R-410A', label: 'R-410A' },
    { value: 'HFC-32', label: 'HFC-32' },
    { value: 'R-1234yf', label: 'R-1234yf' },
    { value: 'SF₆', label: 'SF₆' },
    { value: 'N₂O', label: 'N₂O' },
  ];

  refrigerantTypesM1 = [
    { value: 'HFC-134a', label: 'HFC-134a' },
    { value: 'R-410A', label: 'R-410A' },
    { value: 'HFC-32', label: 'HFC-32' },
    { value: 'R-1234yf', label: 'R-1234yf' },
    { value: 'SF₆', label: 'SF₆' },
  ];
  refrigerantTypesM3 = [
    { value: 'HFC-134a', label: 'HFC-134a' },
    { value: 'R-410A', label: 'R-410A' },
    { value: 'HFC-32', label: 'HFC-32' },
    { value: 'R-1234yf', label: 'R-1234yf' },
    { value: 'SF₆', label: 'SF₆' },
    { value: 'N₂O', label: 'N₂O' },
  ];
  ghgTypes = [
    { value: 'SF₆', label: 'SF₆' },
    { value: 'N₂O', label: 'N₂O' },
    { value: 'CH₄', label: 'CH₄' },
  ];

  fuelUnitsM2: { [key: number]: any[] } = {};
  fuelUnitsM3 = [...this.fuelUnitsM1];

  strings: any = {
    english: {
      question:
        'During the reporting year, did your organisation sell any products whose normal use by customers generates greenhouse gas emissions?',
      note: 'This includes energy-using goods, fuels/feedstocks, products that release GHGs in use, and carbon-removal goods. Exclude manufacturing or disposal emissions.',
      yes: 'Yes',
      no: 'No',
      directMethods: 'Direct methods (mandatory)',
      indirectMethods: 'Indirect methods (non-mandatory)',
      productLabel: 'Product label',
      unitsSold: 'Units sold in reporting year',
      unitType: 'Unit type',
      region: 'Region',
      notes: 'Notes',
      reportingYear: 'Reporting Year',
      productPlaceholder: 'Add product',
      unitsPlaceholder: 'e.g. 10500',
      selectUnit: 'Select unit type',
      selectRegion: 'Select region',
      productGroups: 'Product Groups',
      productGroup: 'Product Group',
      fuelType: 'Fuel type',
      selectFuelType: 'Select fuel type',
      lifetimeFuelUse: 'Lifetime fuel use per unit',
      fuelUsePlaceholder: 'e.g. 450',
      fuelUnit: 'Fuel unit',
      lifetimeElectricityUse: 'Lifetime electricity use (kWh/unit)',
      electricityUsePlaceholder: 'e.g. 3200',
      refrigerantGasType: 'Refrigerant gas type',
      selectRefrigerant: 'Select refrigerant',
      refrigerantCharge: 'Refrigerant charge per unit (kg)',
      refrigerantChargePlaceholder: 'e.g. 0.75',
      refrigerantLeakPct: '% of refrigerant leaked over lifetime',
      leakPctPlaceholder: 'e.g. 15',
      fuelFeedstockType: 'Fuel/feedstock type',
      quantitySold: 'Quantity sold in reporting year',
      quantityPlaceholder: 'e.g. 82000',
      unit: 'Unit',
      gasType: 'Gas type',
      selectGasType: 'Select gas type',
      gasCharge: 'Gas charge per unit (kg)',
      gasChargePlaceholder: 'e.g. 0.250',
      gasLeakPct: '% of gas released over lifetime',
      gasLeakageHint:
        'Typical lifetime leakage for stationary AC is 10–30%; for disposable aerosols 100%',
      lifetimeFunctionalUses: 'Lifetime functional uses per unit',
      functionalUsesPlaceholder: 'e.g. 1000',
      scenarioLabel: 'Scenario label',
      scenarioLabelPlaceholder: 'e.g. Hot-wash 60°C',
      scenarioSharePct: '% of lifetime uses in this scenario',
      pctPlaceholder: 'e.g. 40',
      fuelTypeIfAny: 'Fuel type (if any)',
      fuelPerUse: 'Fuel per use',
      fuelPerUsePlaceholder: 'e.g. 0.15',
      electricityPerUse: 'Electricity per use (kWh)',
      electricityPerUsePlaceholder: 'e.g. 1.30',
      refrigerantLeakedPerUse: 'Refrigerant leaked per use (kg)',
      refrigerantLeakPlaceholder: 'e.g. 0.0003',
      extraGHGPerUse: 'Extra GHG per use (kg)',
      ghgPerUsePlaceholder: 'e.g. 0.05',
      ghgType: 'GHG type',
      selectGHGType: 'Select GHG type',
      intermediateProductLabel: 'Intermediate product label',
      intermediateProductPlaceholder: 'e.g. Li-ion cell P42A',
      unitsIntermediateSold: 'Units of intermediate sold',
      unitsSoldPlaceholder: 'e.g. 5000000',
      lifetimeUsesFinalProduct: 'Lifetime uses of the final product',
      lifetimeUsesPlaceholder: 'e.g. 1000',
      emissionFactorPerUse:
        'Emission factor per use of intermediate (kg CO₂e/use)',
      emissionFactorPlaceholder: 'e.g. 0.0027',
      notesPlaceholder: 'Additional notes about this product group...',
      m1Label: 'Products that directly consume energy',
      m2Label: 'Fuels / feedstocks sold for combustion',
      m3Label: 'GHG-containing products',
      m4Label: 'Indirect energy use (scenario-based)',
      m5Label: 'Intermediate products (allocation)',
      energyVectorRequiredError:
        'At least one energy vector (fuel or electricity) is required',
      scenarioSumError: 'Scenario shares must sum to 100%',
      quantityRequiredError:
        'Quantity sold is required when fuel type is selected',
      unitRequiredError: 'Unit is required when fuel type is selected',
      gasChargeRequiredError:
        'Gas charge is required when gas type is selected',
      gasLeakPctRequiredError:
        'Leak percentage is required when gas charge > 0',
      fuelTypeRequiredError: 'Fuel type is required when fuel quantity > 0',
      fuelUnitRequiredError: 'Fuel unit is required when fuel quantity > 0',
      fuelTypeRequiredError2:
        'Fuel type is required when fuel quantity is entered',
      fuelQtyRequiredError:
        'Fuel quantity is required when fuel type is selected',
      fuelUnitRequiredError2:
        'Fuel unit is required when fuel type is selected',
      refGasTypeRequiredError:
        'Refrigerant gas type is required when refrigerant charge is entered',
      refLeakPctRequiredError:
        'Leak percentage is required when refrigerant charge > 0',
      energyVectorRequiredError2:
        'Either fuel data or electricity use must be provided',
      tooltip:
        'Typical lifetime leakage for stationary AC is 10–30 %; for disposable aerosols 100 %.',
      method_label1: 'Applicability',
      method_label2: 'Required data',
      m1_label1:
        'Products that consume fuel or electricity and may contain a refrigerant gas.',
      m1_label2:
        'For each entry, provide the type of fuel (diesel, gasoline, natural gas, LPG, biomass, hydrogen), the total amount of fuel per unit, and the corresponding unit(L, m³, kg, MJ); or indicate the electricity consumption per unit(kWh) if no fuel is used.If the product contains a refrigerant gas, specify the type, gas charge, and expected leakage percentage.At least one energy source(fuel or electricity) must be provided.',

      m2_label1: 'Sales of fuels or feedstock intended for combustion.',
      m2_label2:
        'Specify the type of fuel, the quantity sold during the period, and the unit of measurement (L, m³, kg, t, kWh, MJ). The unit must be consistent with the physical form of the fuel.',

      m3_label1:
        'Products that contain greenhouse gases or release them during use.',
      m3_label2:
        'Indicate the type of gas (HFC‑134a, R‑410A, R‑32, R‑1234yf, SF₆, N₂O), the quantity of gas per unit (kg), and the leakage percentage over the product’s lifetime.',

      m4_label1:
        'Products whose use involves different scenarios (e.g., washing cycles at different temperatures).',
      m4_label2:
        'Provide the number of uses per unit (cycles, hours, or km) over the product’s lifetime.',
      m4_label12:
        'Define at least one scenario with a label, the percentage share of uses (the sum must be 100%), and for each: fuel per use(with type and unit), electricity per use, any refrigerant loss per use, and other GHG emissions per use (specifying the type of gas).',

      m5_label1:
        'Intermediate goods incorporated into the final product of another company.',
      m5_label2:
        'Indicate the label of the intermediate product, the units sold during the year, the type of unit (items, kg, t, L), the number of uses of the final product, and the emission factor per use of the intermediate good(kg CO₂e per use).',
    },
    italian: {
      question:
        "Durante l'anno di rendicontazione, la vostra organizzazione ha venduto prodotti il cui normale utilizzo da parte dei clienti genera emissioni di gas serra?",
      note: "Ciò include beni che consumano energia, combustibili/materie prime, prodotti che rilasciano gas serra durante l'uso ebeni per la rimozione del carbonio.Escludere le emissioni di produzione o smaltimento.",
      yes: 'Sì',
      no: 'No',
      directMethods: 'Metodi diretti (obbligatori)',
      indirectMethods: 'Metodi indiretti (non obbligatori)',
      productLabel: 'Etichetta prodotto',
      unitsSold: "Unità vendute nell'anno di riferimento",
      unitType: 'Tipo di unità',
      region: 'Regione',
      notes: 'Nota',
      reportingYear: 'Anno di Riferimento',
      productPlaceholder: 'Aggiungi prodotto',
      unitsPlaceholder: 'es. 10500',
      selectUnit: 'Seleziona tipo unità',
      selectRegion: 'Seleziona regione',
      productGroups: 'Gruppi di prodotti',
      productGroup: 'Gruppo prodotto',
      fuelType: 'Tipo carburante',
      selectFuelType: 'Seleziona tipo carburante',
      lifetimeFuelUse: 'Uso carburante a vita (per unità)',
      fuelUsePlaceholder: 'es. 450',
      fuelUnit: 'Unità carburante',
      lifetimeElectricityUse: 'Uso elettricità a vita (kWh/un)',
      electricityUsePlaceholder: 'es. 3200',
      refrigerantGasType: 'Tipo gas refrigerante',
      selectRefrigerant: 'Seleziona refrigerante',
      refrigerantCharge: 'Carica refrigerante per unità (kg)',
      refrigerantChargePlaceholder: 'es. 0.75',
      refrigerantLeakPct: '% refrigerante perso a vita',
      leakPctPlaceholder: 'es. 15',
      fuelFeedstockType: 'Tipo combustibile/materia prima',
      quantitySold: 'Quantità venduta (anno)',
      quantityPlaceholder: 'es. 82000',
      unit: 'Unità',
      gasType: 'Tipo gas',
      selectGasType: 'Seleziona tipo gas',
      gasCharge: 'Carica gas per unità (kg)',
      gasChargePlaceholder: 'es. 0.250',
      gasLeakPct: '% gas rilasciato a vita',
      gasLeakageHint: 'Perdita tipica: 10-30% per AC fissi, 100% per aerosol',
      lifetimeFunctionalUses: 'Usi funzionali a vita (per unità)',
      functionalUsesPlaceholder: 'es. 1000',
      scenarioLabel: 'Etichetta scenario',
      scenarioLabelPlaceholder: 'es. Lavaggio 60°C',
      scenarioSharePct: '% usi in questo scenario',
      pctPlaceholder: 'es. 40',
      fuelTypeIfAny: 'Tipo carburante (se presente)',
      fuelPerUse: 'Carburante per uso',
      fuelPerUsePlaceholder: 'es. 0.15',
      electricityPerUse: 'Elettricità per uso (kWh)',
      electricityPerUsePlaceholder: 'es. 1.30',
      refrigerantLeakedPerUse: 'Refrigerante perso per uso (kg)',
      refrigerantLeakPlaceholder: 'es. 0.0003',
      extraGHGPerUse: 'GHG extra per uso (kg)',
      ghgPerUsePlaceholder: 'es. 0.05',
      ghgType: 'Tipo GHG',
      selectGHGType: 'Seleziona tipo GHG',
      intermediateProductLabel: 'Etichetta prodotto intermedio',
      intermediateProductPlaceholder: 'es. Cella Li-ion P42A',
      unitsIntermediateSold: 'Unità intermedie vendute',
      unitsSoldPlaceholder: 'es. 5000000',
      lifetimeUsesFinalProduct: 'Usi a vita prodotto finale',
      lifetimeUsesPlaceholder: 'es. 1000',
      emissionFactorPerUse: 'Fattore emissione per uso (kg CO₂e/uso)',
      emissionFactorPlaceholder: 'es. 0.0027',
      notesPlaceholder: 'Note aggiuntive...',
      m1Label: 'Prodotti che consumano direttamente energia',
      m2Label: 'Combustibili / materie prime venduti per la combustione',
      m3Label: 'Prodotti contenenti gas serra (GHG)',
      m4Label: 'Uso indiretto di energia (basato su scenari)',
      m5Label: 'Prodotti intermedi (allocazione)',
      energyVectorRequiredError:
        'È richiesto almeno un vettore energetico (carburante o elettricità)',
      scenarioSumError: 'Le quote dello scenario devono sommarsi al 100%',
      quantityRequiredError:
        'La quantità venduta è obbligatoria quando è selezionato un tipo di carburante',
      unitRequiredError:
        "L'unità è obbligatoria quando è selezionato un tipo di carburante",
      gasChargeRequiredError:
        'La carica di gas è obbligatoria quando è selezionato un tipo di gas',
      gasLeakPctRequiredError:
        'La percentuale di perdita è obbligatoria quando la carica di gas > 0',
      fuelTypeRequiredError:
        'Il tipo di carburante è obbligatorio quando la quantità di carburante > 0',
      fuelUnitRequiredError:
        "L'unità di carburante è obbligatoria quando la quantità di carburante > 0",
      fuelTypeRequiredError2:
        'Il tipo di carburante è obbligatorio quando viene inserita una quantità di carburante',
      fuelQtyRequiredError:
        'La quantità di carburante è obbligatoria quando è selezionato un tipo di carburante',
      fuelUnitRequiredError2:
        "L'unità di carburante è obbligatoria quando è selezionato un tipo di carburante",
      refGasTypeRequiredError:
        'Il tipo di gas refrigerante è obbligatorio quando viene inserita una carica di refrigerante',
      refLeakPctRequiredError:
        'La percentuale di perdita è obbligatoria quando la carica di refrigerante > 0',
      energyVectorRequiredError2:
        'Devono essere forniti dati sul carburante o sull’uso di elettricità',
      tooltip:
        "La perdita tipica durante la vita utile per l'AC stazionario è del 10–30%; per gli aerosol usa e getta è del 100%.",
      method_label1: 'Applicabilità',
      method_label2: 'Dati richiesti',
      m1_label1:
        'prodotti che consumano carburante o elettricità e che possono contenere un gas refrigerante.',
      m1_label2:
        'per ogni riga fornire il tipo di carburante (diesel, benzina, gas naturale, GPL, biomassa, idrogeno), la quantità totale di carburante per unità e la relativa unità(L, m³, kg, MJ); oppure indicare il consumo di elettricità per unità(kWh) se non si usa carburante.Se il prodotto contiene un gas refrigerante, indicare il tipo, la carica di gas e la percentuale di perdita prevista.È obbligatorio compilare almeno una fonte energetica(carburante o elettricità).',
      m2_label1:
        'vendite di combustibili o feedstock destinati alla combustione.',
      m2_label2:
        'specificare il tipo di carburante, la quantità venduta nel periodo e l’unità di misura (L, m³, kg, t, kWh, MJ). L’unità deve essere coerente con la forma fisica del carburante.',
      m3_label1:
        'prodotti che contengono gas serra o che li rilasciano durante l’uso.',
      m3_label2:
        'indicare il tipo di gas (HFC‑134a, R‑410A, R‑32, R‑1234yf, SF₆, N₂O), la quantità di gas per unità (kg) e la percentuale di perdita durante la vita utile.',
      m4_label1:
        'prodotti il cui utilizzo comporta diversi scenari (es. cicli di lavaggio a temperature differenti).',
      m4_label2:
        'Fornire il numero di utilizzi per unità (cicli, ore o km) durante la vita utile.',
      m4_label12:
        'Definire almeno uno scenario con una etichetta, la quota percentuale di utilizzi (la somma deve essere 100 %), e per ciascuno: carburante per uso(con tipo e unità), elettricità per uso, eventuale perdita di refrigerante per uso e altre emissioni di GHG per uso(specificando il tipo di gas).',
      m5_label1:
        'beni intermedi incorporati nel prodotto finale di un’altra azienda.',
      m5_label2:
        'indicare l’etichetta del prodotto intermedio, le unità vendute nell’anno, il tipo di unità (items, kg, t, L), il numero di utilizzi del prodotto finale e il fattore di emissione per uso del bene intermedio(kg CO₂e per utilizzo).',
    },
  };

  ngOnInit() {
    this.changeLabels();
    const stored = localStorage.getItem('categories');
    if (stored) {
      const parsed = JSON.parse(stored);
      const saved = parsed?.[4]?.questions?.[0]?.answers;

      if (saved) {
        this.usage = saved.usage ?? false;
        this.cat5_1_direct_total = saved.cat5_1_direct_total ?? 0;

        if (Array.isArray(saved.productGroups)) {
          saved.productGroups.forEach((group: any) => {
            this.productGroups.push(this.createProductGroup(group));
          });
        }
      }
    }

    this.form.valueChanges.subscribe(() => this.save());
  }
  changeLabels() {
    if (this.language === 'english') {
      this.fuelTypesM1 = [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'natural_gas', label: 'Natural Gas' },
        { value: 'coal', label: 'Coal' },
        { value: 'LPG', label: 'LPG' },
        { value: 'wood_pellets_bio_100', label: 'Wood pellets (bio 100%)' },
        { value: 'hydrogen', label: 'Hydrogen' },
      ];
      this.fuelTypesM2 = [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'natural_gas', label: 'Natural Gas' },
        { value: 'LPG', label: 'LPG' },
        { value: 'coal', label: 'Coal' },
        { value: 'kerosene', label: 'Kerosene' },
        { value: 'heavy_fuel_oil', label: 'Heavy Fuel Oil' },
        { value: 'wood_pellets_bio_100', label: 'Wood Pellets (100% bio)' },
        { value: 'hydrogen', label: 'Hydrogen' },
      ];
    } else {
      this.fuelTypesM1 = [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Benzina' },
        { value: 'natural_gas', label: 'Gas naturale' },
        { value: 'coal', label: 'Carbone' },
        { value: 'LPG', label: 'GPL' },
        { value: 'wood_pellets_bio_100', label: 'Pellet di legno (bio 100%)' },
        { value: 'hydrogen', label: 'Idrogeno' },
      ];
      this.fuelTypesM2 = [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Benzina' },
        { value: 'natural_gas', label: 'Gas naturale' },
        { value: 'LPG', label: 'GPL' },
        { value: 'coal', label: 'Carbone' },
        { value: 'kerosene', label: 'Cherosene' },
        { value: 'heavy_fuel_oil', label: 'Olio combustibile pesante' },
        { value: 'wood_pellets_bio_100', label: 'Pellet di legno (100% bio)' },
        { value: 'hydrogen', label: 'Idrogeno' },
      ];
    }
  }
  setUsage(value: boolean) {
    this.usage = value;
    if (!value) {
      this.productGroups.clear();
    } else {
      this.addProductGroup();
    }
    this.save();
  }

  addProductGroup() {
    const newGroup = this.createProductGroup();
    this.productGroups.push(newGroup);
    this.save();
  }

  removeProductGroup(index: number) {
    this.productGroups.removeAt(index);
    this.save();
  }

  createProductGroup(data: any = {}): FormGroup {
    const group = this.fb.group({
      product_label: [data.product_label ?? '', [Validators.maxLength(250)]],
      N_sold: [
        data.N_sold ?? null,
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern('^[0-9]+$'),
        ],
      ],
      units_sold_unit: [data.units_sold_unit ?? '', Validators.required],
      region: [data.region ?? '', Validators.required],
      reportingYear: [data.reportingYear ?? '', Validators.required],
      note: [data.note ?? '', [Validators.maxLength(500)]],

      // Method selection
      use_m1: [data.use_m1 ?? false],
      use_m2: [data.use_m2 ?? false],
      use_m3: [data.use_m3 ?? false],
      use_m4: [data.use_m4 ?? false],
      use_m5: [data.use_m5 ?? false],

      // Method 1
      m1_methods: this.fb.array([], [this.validateM1Methods]),

      // Method 2
      m2_methods: this.fb.array([]),

      // Method 3
      m3_methods: this.fb.array([]),

      // Method 4
      U_life: [data.U_life ?? null, [Validators.required, Validators.min(0)]],
      m4_methods: this.fb.array([], [this.validateScenarioSum]),

      // Method 5
      intermed_label: [
        data.intermed_label ?? '',
        [Validators.required, Validators.maxLength(250)],
      ],
      intermed_sold: [
        data.intermed_sold ?? null,
        [Validators.required, Validators.min(0)],
      ],
      intermed_unit: [data.intermed_unit ?? '', Validators.required],
      U_life_final: [
        data.U_life_final ?? null,
        [Validators.required, Validators.min(0)],
      ],
      EF_per_use_intermed: [
        data.EF_per_use_intermed ?? null,
        [Validators.required, Validators.min(0)],
      ],

      // Calculation results
      calculationResults: this.fb.group({
        m1: [data.calculationResults?.m1 ?? 0],
        m2: [data.calculationResults?.m2 ?? 0],
        m3: [data.calculationResults?.m3 ?? 0],
        m4: [data.calculationResults?.m4 ?? 0],
        m5: [data.calculationResults?.m5 ?? 0],
        total: [data.calculationResults?.total ?? 0],
      }),
    });

    // Add existing methods
    if (data.m1_methods) {
      data.m1_methods.forEach((m: any) =>
        (group.get('m1_methods') as FormArray).push(this.createM1Method(m)),
      );
    }
    if (data.m2_methods) {
      data.m2_methods.forEach((m: any) =>
        (group.get('m2_methods') as FormArray).push(this.createM2Method(m)),
      );
    }
    if (data.m3_methods) {
      data.m3_methods.forEach((m: any) =>
        (group.get('m3_methods') as FormArray).push(this.createM3Method(m)),
      );
    }
    if (data.m4_methods) {
      data.m4_methods.forEach((m: any) =>
        (group.get('m4_methods') as FormArray).push(this.createM4Method(m)),
      );
    }

    return group;
  }

  // Method validators
  validateM1Methods: ValidatorFn = (
    control: AbstractControl,
  ): { [key: string]: any } | null => {
    const array = control as FormArray;
    if (array.length === 0) return null;

    for (const method of array.controls) {
      const methodGroup = method as FormGroup;

      // Check M1 validations if this is an M1 method
      if (methodGroup.contains('fuel_type')) {
        const fuelType = methodGroup.get('fuel_type')?.value;
        const fuelQty = methodGroup.get('fuel_lifetime_quantity')?.value;
        const elec = methodGroup.get('elec_lifetime_kwh')?.value;

        if ((fuelQty && !fuelType) || (!fuelQty && !elec)) {
          return { energyVectorRequired: true };
        }
      }

      // Check M2 validations if this is an M2 method
      if (methodGroup.contains('fuel_qty')) {
        const fuelType = methodGroup.get('fuel_type')?.value;
        const fuelQty = methodGroup.get('fuel_qty')?.value;
        const fuelUnit = methodGroup.get('fuel_unit')?.value;

        if (fuelType) {
          if (!fuelQty) {
            return { fuelQtyRequired: true };
          }
          if (!fuelUnit) {
            return { fuelUnitRequired: true };
          }
        }
      }

      // Check M3 validations if this is an M3 method
      if (methodGroup.contains('gas_type')) {
        const gasType = methodGroup.get('gas_type')?.value;
        const gasCharge = methodGroup.get('gas_charge_kg')?.value;
        const gasLeakPct = methodGroup.get('gas_leak_pct')?.value;

        if (gasType) {
          if (gasCharge === null || gasCharge === '') {
            return { gasChargeRequired: true };
          }
          if (gasCharge > 0 && (gasLeakPct === null || gasLeakPct === '')) {
            return { gasLeakPctRequired: true };
          }
        }
      }

      // Check M4 validations if this is an M4 method
      if (methodGroup.contains('scn_label')) {
        // Scenario sum validation
        const total = methodGroup
          .get('m4_methods')
          ?.value?.reduce(
            (sum: number, m: any) => sum + (m.scn_share_pct || 0),
            0,
          );
        if (Math.round(total) !== 100) {
          return { scenarioSumInvalid: true };
        }

        // Fuel type/unit validation
        const fuelQty = methodGroup.get('scn_fuel_qty')?.value;
        const fuelType = methodGroup.get('scn_fuel_type')?.value;
        const fuelUnit = methodGroup.get('scn_fuel_unit')?.value;

        if (fuelQty > 0) {
          if (!fuelType) {
            return { fuelTypeRequired: true };
          }
          if (!fuelUnit) {
            return { fuelUnitRequired: true };
          }
        }
      }

      // Check M5 validations if this is an M5 method
      if (methodGroup.contains('intermed_label')) {
        const intermedLabel = methodGroup.get('intermed_label')?.value;
        const intermedSold = methodGroup.get('intermed_sold')?.value;
        const intermedUnit = methodGroup.get('intermed_unit')?.value;
        const uLifeFinal = methodGroup.get('U_life_final')?.value;
        const efPerUse = methodGroup.get('EF_per_use_intermed')?.value;

        if (!intermedLabel) {
          return { intermedLabelRequired: true };
        }
        if (!intermedSold) {
          return { intermedSoldRequired: true };
        }
        if (!intermedUnit) {
          return { intermedUnitRequired: true };
        }
        if (!uLifeFinal) {
          return { uLifeFinalRequired: true };
        }
        if (!efPerUse) {
          return { efPerUseRequired: true };
        }
      }
    }
    return null;
  };

  validateScenarioSum: ValidatorFn = (
    control: AbstractControl,
  ): { [key: string]: any } | null => {
    // Check if this is a FormArray (for m4_methods)
    if (control instanceof FormArray) {
      const array = control as FormArray;
      if (array.length === 0) return null;

      const total = array.controls.reduce((sum, method) => {
        const methodGroup = method as FormGroup;
        return sum + (methodGroup.get('scn_share_pct')?.value || 0);
      }, 0);

      return Math.round(total) === 100 ? null : { scenarioSumInvalid: true };
    }

    // For FormGroup (individual scenario)
    const methodGroup = control as FormGroup;
    const sharePct = methodGroup.get('scn_share_pct')?.value || 0;

    // We can't validate the sum here for a single scenario
    // Just validate the individual percentage is valid
    if (sharePct < 0 || sharePct > 100) {
      return { invalidSharePct: true };
    }

    return null;
  };

  createM1Method(data: any = {}): FormGroup {
    return this.fb.group(
      {
        fuel_type: [data.fuel_type ?? ''],
        fuel_lifetime_quantity: [
          data.fuel_lifetime_quantity ?? null,
          [Validators.min(0)],
        ],
        fuel_lifetime_unit: [data.fuel_lifetime_unit ?? ''],
        elec_lifetime_kwh: [
          data.elec_lifetime_kwh ?? null,
          [Validators.min(0)],
        ],
        ref_gas_type: [data.ref_gas_type ?? ''],
        ref_charge_kg: [data.ref_charge_kg ?? null, [Validators.min(0)]],
        ref_leak_pct: [
          data.ref_leak_pct ?? null,
          [Validators.min(0), Validators.max(100)],
        ],
      },
      { validators: this.validateM1Method },
    );
  }

  validateM1Method(group: FormGroup): { [key: string]: any } | null {
    const fuelType = group.get('fuel_type')?.value;
    const fuelQty = group.get('fuel_lifetime_quantity')?.value;
    const fuelUnit = group.get('fuel_lifetime_unit')?.value;
    const elec = group.get('elec_lifetime_kwh')?.value;
    const refGasType = group.get('ref_gas_type')?.value;
    const refCharge = group.get('ref_charge_kg')?.value;
    const refLeakPct = group.get('ref_leak_pct')?.value;

    // Fuel validation
    if (fuelQty && !fuelType) {
      return { fuelTypeRequired: true };
    }
    if (fuelType && !fuelQty) {
      return { fuelQtyRequired: true };
    }
    if (fuelType && !fuelUnit) {
      return { fuelUnitRequired: true };
    }

    // Energy vector validation - either fuel or electricity must be provided
    if (!fuelQty && !elec) {
      return { energyVectorRequired: true };
    }

    // Refrigerant validation
    if (refCharge && !refGasType) {
      return { refGasTypeRequired: true };
    }
    if (refCharge > 0 && !refLeakPct) {
      return { refLeakPctRequired: true };
    }

    return null;
  }

  createM2Method(data: any = {}): FormGroup {
    return this.fb.group(
      {
        fuel_type: [data.fuel_type ?? '', Validators.required],
        fuel_qty: [data.fuel_qty ?? null, [Validators.min(1)]],
        fuel_unit: [data.fuel_unit ?? ''], // Removed default required validator
      },
      { validators: this.validateM2Method },
    );
  }
  validateM2Method(group: FormGroup): { [key: string]: any } | null {
    const fuelType = group.get('fuel_type')?.value;
    const fuelQty = group.get('fuel_qty')?.value;
    const fuelUnit = group.get('fuel_unit')?.value;

    if (fuelType) {
      if (!fuelQty) {
        return { fuelQtyRequired: true };
      }
      if (!fuelUnit) {
        return { fuelUnitRequired: true };
      }
    }
    return null;
  }
  createM3Method(data: any = {}): FormGroup {
    return this.fb.group(
      {
        gas_type: [data.gas_type ?? '', Validators.required],
        gas_charge_kg: [
          data.gas_charge_kg ?? null,
          [Validators.min(0)], // Required validator will be handled by the group validator
        ],
        gas_leak_pct: [
          data.gas_leak_pct ?? 90,
          [Validators.min(0), Validators.max(100)],
        ],
      },
      { validators: this.validateM3Method },
    ); // Add group validator
  }
  validateM3Method(group: FormGroup): { [key: string]: any } | null {
    const gasType = group.get('gas_type')?.value;
    const gasCharge = group.get('gas_charge_kg')?.value;
    const gasLeakPct = group.get('gas_leak_pct')?.value;

    if (gasType) {
      if (gasCharge === null || gasCharge === '') {
        return { gasChargeRequired: true };
      }
      if (gasCharge > 0 && (gasLeakPct === null || gasLeakPct === '')) {
        return { gasLeakPctRequired: true };
      }
    }
    return null;
  }
  createM4Method(data: any = {}): FormGroup {
    return this.fb.group(
      {
        scn_label: [
          data.scn_label ?? '',
          [Validators.required, Validators.maxLength(50)],
        ],
        scn_share_pct: [
          data.scn_share_pct ?? null,
          [Validators.required, Validators.min(0), Validators.max(100)],
        ],
        scn_fuel_type: [data.scn_fuel_type ?? ''],
        scn_fuel_qty: [data.scn_fuel_qty ?? null, [Validators.min(0)]],
        scn_fuel_unit: [data.scn_fuel_unit ?? ''],
        scn_elec_kwh: [data.scn_elec_kwh ?? null, [Validators.min(0)]],
        scn_ref_leak_kg: [data.scn_ref_leak_kg ?? null, [Validators.min(0)]],
        scn_ref_gas_type: [data.scn_ref_gas_type ?? ''],
        scn_ghg_kg: [data.scn_ghg_kg ?? null, [Validators.min(0)]],
        scn_ghg_type: [data.scn_ghg_type ?? ''],
      },
      {
        validators: [
          this.validateM4Method,
          (control) => this.validateScenarioSum(control as FormGroup), // Cast to FormGroup
        ],
      },
    );
  }
  validateM4Method(group: FormGroup): { [key: string]: any } | null {
    const fuelQty = group.get('scn_fuel_qty')?.value;
    const fuelType = group.get('scn_fuel_type')?.value;
    const fuelUnit = group.get('scn_fuel_unit')?.value;

    if (fuelQty > 0) {
      if (!fuelType) {
        return { fuelTypeRequired: true };
      }
      if (!fuelUnit) {
        return { fuelUnitRequired: true };
      }
    }
    return null;
  }
  addMethod(group: AbstractControl, methodName: string, method: FormGroup) {
    const methodsArray = this.getMethodArray(group, methodName);
    methodsArray.push(method);
    this.save();
  }

  removeMethod(group: AbstractControl, methodName: string, index: number) {
    const methodsArray = this.getMethodArray(group, methodName);
    methodsArray.removeAt(index);
    this.save();
  }

  getMethodArray(group: AbstractControl, methodName: string): FormArray {
    const formGroup = group as FormGroup;
    let array = formGroup.get(methodName) as FormArray;
    if (!array) {
      array = this.fb.array([]);
      formGroup.setControl(methodName, array);
    }
    return array;
  }

  getFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  getFormControl(group: AbstractControl, controlName: string): FormControl {
    const control = (group as FormGroup).get(controlName);
    return control as FormControl;
  }

  formatAmount(i: number) {
    const formGroup = this.productGroups.at(i) as FormGroup;
    const control = formGroup.get('N_sold');
    if (!control) return;
    const rawValue = control.value?.toString() ?? '';
    const numeric = rawValue.replace(/\D+/g, '');
    if (!numeric) {
      control.setValue('', { emitEvent: false });
      return;
    }
    const intValue = parseInt(numeric, 10);
    control.setValue(intValue, { emitEvent: false });
  }

  updateFuelUnitsM2(
    group: AbstractControl,
    controlName: string,
    index: number,
  ) {
    const formGroup = group as FormGroup;
    const fuelControl = formGroup.get(controlName);
    const unitControl = formGroup.get('fuel_unit');
    const selectedFuel = fuelControl?.value;

    if (selectedFuel && this.fuelTypeCategoryMap[selectedFuel]) {
      const fuelCategory = this.fuelTypeCategoryMap[selectedFuel];
      this.fuelUnitsM2[index] = this.unitCategoryMap[fuelCategory];

      // Reset unit if current selection is not available for this fuel type
      const currentUnit = unitControl?.value;
      if (
        currentUnit &&
        !this.fuelUnitsM2[index].some((u) => u.value === currentUnit)
      ) {
        unitControl?.patchValue('');
      }
    } else {
      // Default to all units if no fuel selected or unknown fuel type
      this.fuelUnitsM2[index] = [...this.fuelUnits];
    }
    this.save();
  }

  updateFuelUnitsM3(group: AbstractControl, controlName: string) {
    const formGroup = group as FormGroup;
    const fuelControl = formGroup.get(controlName);
    const unitControl = formGroup.get('fuel_lifetime_unit');
    const selectedFuel = fuelControl?.value;

    if (selectedFuel && this.fuelTypeCategoryMap[selectedFuel]) {
      const fuelCategory = this.fuelTypeCategoryMap[selectedFuel];
      this.fuelUnitsM3 = this.unitCategoryMap[fuelCategory];

      // Reset unit if current selection is not available for this fuel type
      const currentUnit = unitControl?.value;
      if (
        currentUnit &&
        !this.fuelUnitsM3.some((u) => u.value === currentUnit)
      ) {
        unitControl?.patchValue('');
      }
    } else {
      // Default to all units if no fuel selected or unknown fuel type
      this.fuelUnitsM3 = [...this.fuelUnits];
    }
    this.save();
  }

  toggleCheckbox(group: AbstractControl, controlName: string): void {
    const formGroup = group as FormGroup;
    const control = formGroup.get(controlName);

    if (control) {
      control.setValue(!control.value);
      const methodsArrayName = controlName.replace('use_', '') + '_methods';
      const methodsArray = formGroup.get(methodsArrayName) as FormArray;

      if (!control.value && methodsArray) {
        formGroup.get('U_life')?.reset();
        while (methodsArray.length) {
          methodsArray.removeAt(0);
        }
      }

      if (control.value && methodsArray && methodsArray.length === 0) {
        switch (methodsArrayName) {
          case 'm1_methods':
            methodsArray.push(this.createM1Method());
            break;
          case 'm2_methods':
            methodsArray.push(this.createM2Method());
            break;
          case 'm3_methods':
            methodsArray.push(this.createM3Method());
            break;
          case 'm4_methods':
            methodsArray.push(this.createM4Method());
            break;
        }
      }
    }

    this.save();
  }

  save() {
    const answers = {
      usage: this.usage,
      cat5_1_direct_total: this.cat5_1_direct_total,
      productGroups: this.form.value.productGroups,
    };

    this.categories[4].questions[0].answers = answers as any;
    this.categoryService.persistCategories(this.categories);
  }
}
